import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import {
  useCompanies,
  useRemoveSessionType,
  useSaveSessionType,
  useServiceTypes,
  useSessionTypes,
} from '@/api/hooks'
import type { SessionTypeView } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner, TimeField } from '@/components/common'

const BLANK: SessionTypeView = {
  id: '',
  code: '',
  name: '',
  serviceTypes: [],
  chargedByPax: true,
  startTime: '12:00:00',
  durationMinutes: 240,
  coversFrom: '12:00:00',
  sortOrder: 99,
  active: true,
}

/** The API keeps HH:mm:ss; a time field on screen wants HH:mm. */
const asField = (time: string) => (time ?? '').slice(0, 5)
const asStored = (time: string) => (time.length === 5 ? `${time}:00` : time)

/**
 * The sittings the company sells by - breakfast, lunch, hi-tea, dinner, and
 * whatever else an office actually sells.
 *
 * <p>Four of these were written into the quotation screen and into the pricing,
 * so an office selling a tiffin round, a midnight buffet or nothing but lunch
 * could not say so without a release.</p>
 *
 * <p>One list, offered to every line of business. A sitting is a time of day,
 * not a property of what is being sold: nobody serves breakfast differently
 * because it happens to be a banquet, and holding one row per service only
 * meant the same meal stored five times and five places to correct its
 * hours.</p>
 */
export function SessionsTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const target = companyId ?? ownCompanyId
  const { data: companies } = useCompanies()
  const canPickCompany = can('platform.admin') && (companies ?? []).length > 1

  const { data: services } = useServiceTypes({ companyId, includeInactive: true })
  const { data: sittings, error } = useSessionTypes({ companyId, includeInactive: true })
  const save = useSaveSessionType()
  const remove = useRemoveSessionType()

  const [rows, setRows] = useState<SessionTypeView[]>([])
  const writable = canWrite('config')

  useEffect(() => {
    setRows((sittings ?? []).map((row) => ({ ...row })))
  }, [sittings])

  const update = (index: number, patch: Partial<SessionTypeView>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)))

  const nameOf = (code: string) =>
    (services ?? []).find((service) => service.code === code)?.name ?? code

  // Which services would be asked which sitting it is as things stand, so the
  // effect of assigning and unassigning is visible without leaving the screen.
  const asked = new Set(rows.filter((row) => row.active).flatMap((row) => row.serviceTypes))
  const silent = (services ?? []).filter((service) => service.active && !asked.has(service.code))

  return (
    <>
      <ErrorBanner error={error ?? save.error ?? remove.error} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {canPickCompany ? (
            <TextField
              select
              size="small"
              label="Company"
              value={target ?? ''}
              onChange={(event) => setCompanyId(event.target.value)}
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Company' }}
            >
              {(companies ?? []).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            The sessions this business sells by, and which lines of business each is sold under.
          </Typography>
        </Stack>
        {writable ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRows((current) => [...current, { ...BLANK }])}
          >
            New session
          </Button>
        ) : null}
      </Stack>

      {silent.length > 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Not asked which session it is: {silent.map((service) => service.name).join(', ')}. Assign
          a session below to start asking.
        </Alert>
      ) : null}

      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1280 }}>
        <TableHead>
          <TableRow>
            <TableCell width={150}>Code</TableCell>
            <TableCell width={180}>Called</TableCell>
            <TableCell width={260}>Sold under</TableCell>
            <TableCell width={110}>By head</TableCell>
            <TableCell width={130}>Starts</TableCell>
            <TableCell width={120}>Runs (min)</TableCell>
            <TableCell width={140}>Counts from</TableCell>
            <TableCell width={100}>Order</TableCell>
            <TableCell width={90}>Offered</TableCell>
            <TableCell align="right" width={110}>
              &nbsp;
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            // The code is what every quotation, order and kitchen request has
            // already recorded, so it is fixed once anything has been sold
            // under it. Rename it instead.
            const codeFixed = Boolean(row.id)
            return (
              <TableRow key={row.id || `new-${index}`} sx={{ opacity: row.active ? 1 : 0.55 }}>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.code}
                    disabled={!writable || codeFixed}
                    onChange={(event) =>
                      update(index, {
                        code: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                      })
                    }
                    inputProps={{ 'aria-label': `Code ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.name}
                    disabled={!writable}
                    onChange={(event) => update(index, { name: event.target.value })}
                    inputProps={{ 'aria-label': `Called ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  {/* The sitting is one row; which services sell it is the
                      question this column answers. None ticked and nothing is
                      sold under it, which is how a session is retired from one
                      line of business without being withdrawn from the rest. */}
                  <Select
                    multiple
                    fullWidth
                    size="small"
                    displayEmpty
                    disabled={!writable}
                    value={row.serviceTypes}
                    onChange={(event) =>
                      update(index, {
                        serviceTypes:
                          typeof event.target.value === 'string'
                            ? event.target.value.split(',')
                            : event.target.value,
                      })
                    }
                    inputProps={{ 'aria-label': `Sold under ${index + 1}` }}
                    renderValue={(chosen) =>
                      chosen.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nothing yet
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {chosen.map((code) => (
                            <Chip key={code} size="small" label={nameOf(code)} />
                          ))}
                        </Box>
                      )
                    }
                  >
                    {(services ?? []).map((service) => (
                      <MenuItem key={service.code} value={service.code}>
                        <Checkbox
                          size="small"
                          checked={row.serviceTypes.includes(service.code)}
                        />
                        <ListItemText primary={service.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  {/* A wedding lunch is a package times the pax, and leaf or
                      buffet is part of what is sold. A tray of sweets is not:
                      there is no pax to give and nothing is plated, so both
                      questions are dropped and it is quoted as lines. */}
                  <Tooltip title="On: priced by the head, and asked for pax and how it is served. Off: quoted as lines of quantity times rate, and neither is asked">
                    <Switch
                      size="small"
                      checked={row.chargedByPax}
                      disabled={!writable}
                      onChange={(event) => update(index, { chargedByPax: event.target.checked })}
                      inputProps={{ 'aria-label': `By head ${index + 1}` }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Offered as the time on a quotation for this session">
                    <TimeField
                      value={asField(row.startTime)}
                      disabled={!writable}
                      onChange={(value) => update(index, { startTime: asStored(value) })}
                      ariaLabel={`Starts ${index + 1}`}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="How long it runs where a quotation gave no end time">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={row.durationMinutes}
                      disabled={!writable}
                      onChange={(event) =>
                        update(index, { durationMinutes: Number(event.target.value) })
                      }
                      inputProps={{ 'aria-label': `Runs ${index + 1}`, min: 15, max: 1440 }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="The hour from which a bare clock time counts as this session rather than the one before it">
                    <TimeField
                      value={asField(row.coversFrom)}
                      disabled={!writable}
                      onChange={(value) => update(index, { coversFrom: asStored(value) })}
                      ariaLabel={`Counts from ${index + 1}`}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={row.sortOrder}
                    disabled={!writable}
                    onChange={(event) => update(index, { sortOrder: Number(event.target.value) })}
                    inputProps={{ 'aria-label': `Order ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Off means it is no longer sold; work raised under it still names it">
                    <Switch
                      size="small"
                      checked={row.active}
                      disabled={!writable}
                      onChange={(event) => update(index, { active: event.target.checked })}
                      inputProps={{ 'aria-label': `Offered ${index + 1}` }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  {writable ? (
                    <>
                      <Button
                        size="small"
                        disabled={save.isPending || !row.code.trim() || !row.name.trim()}
                        onClick={() =>
                          save.mutate({
                            id: row.id || undefined,
                            companyId,
                            code: row.code,
                            name: row.name,
                            serviceTypes: row.serviceTypes,
                            chargedByPax: row.chargedByPax,
                            startTime: asField(row.startTime),
                            durationMinutes: row.durationMinutes,
                            coversFrom: asField(row.coversFrom),
                            sortOrder: row.sortOrder,
                            active: row.active,
                          })
                        }
                      >
                        Save
                      </Button>
                      {row.id ? (
                        <Tooltip title="Withdraw">
                          <IconButton
                            size="small"
                            onClick={() => remove.mutate({ id: row.id, companyId })}
                            aria-label={`Withdraw ${row.name}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}
